import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import NewStudentForm from './NewStudentForm';
import { PageSection } from '../PageSection';
import { NewCheckInFormButton } from '../NewCheckInForm';
import { Baseline } from '../shared/components';
import ResponseLabel from '../ResponseLabel';
import authorizedFetch, { authorizedDelete } from '../api-authorization/AuthorizedFetch';
import { safeParseResponse } from '../../utils';
import { ClassName, ClassViewTable } from './ClassView.styles';
import { StyledButton } from '../shared/styled';
import { BaselineIcon, CheckInIcon, StudentIcon, TeacherIcon } from '../../icons';

const ClassView = () => {
    const { id } = useParams();
    const history = useHistory();
    
    const [isStudentModalOpen, setIsStudentModalOpen] = useState(false);
    const [nameOfClass, setNameOfClass] = useState(null);
    const [classBaseline, setClassBaseline] = useState(null);
    const [existingCheckIns, setExistingCheckIns] = useState(null); 
    const [students, setStudents] = useState(null);
    const [teachers, setTeachers] = useState(null);

    const fetchData = useCallback(async () => {
        const response = await authorizedFetch(`api/classes/${id}`);
        const { name, baseline, checkInHistory, users = [] } = await safeParseResponse(response) || {};
        setNameOfClass(name);
        setClassBaseline(baseline);
        setExistingCheckIns(checkInHistory);
        setStudents(users?.filter(x => x.role === 'Student'));
        setTeachers(users?.filter(x => x.role === 'Teacher'));
    }, [id, setNameOfClass, setClassBaseline, setExistingCheckIns, setStudents, setTeachers]);

    useEffect(() => {
        if (id) fetchData();
    }, [id, fetchData]);

    const toggle = () => setIsStudentModalOpen(!isStudentModalOpen);

    const gotoCheckIn = id => history.push(`/check-ins/${id}/summary`);

    const gotoStudent = userId => history.push(`/users/${userId}`);

    const removeStudent = async (e, userId) => {
        e.stopPropagation();
        await authorizedDelete(`api/classes/${id}/students/${userId}`);
        fetchData();
    };

    return (
        <>
            <ClassName>{nameOfClass}</ClassName>

            {isStudentModalOpen && (
                <NewStudentForm 
                    classId={id} 
                    toggle={toggle} 
                    isOpen={isStudentModalOpen} 
                    onSave={fetchData} 
                />
            )}
    
            <PageSection title="Class Baseline" Icon={BaselineIcon}>
                <tr>
                    <td>
                        <Baseline value={classBaseline} disabled />
                    </td>
                </tr>
            </PageSection>
            
            <PageSection title="Check In History" Icon={CheckInIcon} Button={<NewCheckInFormButton>New Check In</NewCheckInFormButton>}>
                <ClassViewTable>
                    <tr>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Average Response</th>
                    </tr>
                    {existingCheckIns?.length > 0 ? existingCheckIns?.map(({ checkInId, classAverage, createdOn }) => (
                        <>

                            <tr onClick={() => gotoCheckIn(checkInId)}>
                                <td>{new Date(createdOn).toLocaleString('en-us', { dateStyle: 'short' })}</td>
                                <td>{new Date(createdOn).toLocaleString('en-us', { timeStyle: 'short' })}</td>
                                <td>{classAverage}</td>
                                <td><StyledButton onClick={() => gotoCheckIn(checkInId)} value={checkInId}>View</StyledButton></td>
                            </tr>
                        </>
                    )) : (
                        <tr>
                            <td colSpan={3}><em>No Check Ins</em></td>
                        </tr>
                    )}
                </ClassViewTable>
            </PageSection>

            <PageSection title="Teachers" Icon={TeacherIcon} Button={<StyledButton onClick={toggle}>New Teacher</StyledButton>}>
                <ClassViewTable>
                    {teachers?.length > 0 ? 
                        teachers?.map(({ baseline: studentBaseline, applicationUserId, firstName, lastName }) => (
                            <>
                                <tr>
                                    <th>Name</th>
                                    <th>Baseline State</th>
                                </tr>
                                <tr>
                                    <td onClick={() => gotoStudent(applicationUserId)}>
                                        {`${firstName} ${lastName}`}
                                    </td>
                                    <td>
                                         <ResponseLabel response={studentBaseline} />
                                    </td>
                                    <td>
                                        <StyledButton color="red" hoverColor="red" onClick={e => removeStudent(e, applicationUserId)}>
                                            Remove
                                        </StyledButton>
                                    </td>
                                </tr>
                            </>
                        )) : (
                            <em>No Teachers</em>
                        )
                    }
                </ClassViewTable>
            </PageSection>

            <PageSection title="Students" Icon={StudentIcon} Button={<StyledButton onClick={toggle}>New Student</StyledButton>}>
                {students?.length > 0 ? (
                    <ClassViewTable>
                        {students?.map(({ baseline: studentBaseline, applicationUserId, firstName, lastName }) => (
                            <>
                                <tr>
                                    <th>Name</th>
                                    <th>Baseline State</th>
                                </tr>
                                <tr>
                                    <td onClick={() => gotoStudent(applicationUserId)}>
                                        {`${firstName} ${lastName}`}
                                    </td>
                                    <td>
                                        <ResponseLabel response={studentBaseline} />
                                    </td>
                                    <StyledButton color="red" hoverColor="red" onClick={e => removeStudent(e, applicationUserId)}>
                                        Remove
                                    </StyledButton>
                                </tr>
                            </>
                        ))}
                    </ClassViewTable>
                ) : (
                    <em>No Students</em>
                )}
            </PageSection>
        </>
    );
}

export default ClassView;